import { EnvironmentService } from './../environment/environment.service';
import { HttpHeaders, HttpClient } from "@angular/common/http";
import { Observable, retry, throwError, throwError as observableThrowError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { environment } from "environments/environment";
import { Injector, Inject, Injectable } from '@angular/core';
import Utils from "@app/shared/utils/Utils";
import { OmniServiceBaseResponse } from "@app/shared/models/basic.models";

@Injectable({ providedIn: 'root' })
export abstract class BaseHttp {

    protected http: HttpClient;
    protected envSvc: EnvironmentService;

    private headersMap: { [key: string]: string } = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };

    private retryCount: number = 1;

    protected headers: HttpHeaders = new HttpHeaders();

    constructor(@Inject(Injector) protected injector: Injector) {
        this.http = injector.get(HttpClient);
        this.envSvc = injector.get(EnvironmentService);

        this.retryCount = Number(this.envSvc.getProperty("retyCount")) || this.retryCount;

        this.initStaticHeaders();
        this.addHeader('Oc-ApplicationVersion', this.envSvc.getProperty("applicationVersion"));

        let platform = this.envSvc.getProperty("applicationPlatform");
        if (Utils.isIphone()) {
          platform = "iOS";
        }
        else if (Utils.isAndroid()) {
          platform = "Android";
        }
        this.addHeader('Oc-ApplicationPlatform', platform);
    }

    /*** Initialize headers which are not going to change during the lifetime of the App */
    private initStaticHeaders() {
        this.headers = new HttpHeaders();
        for (const key in this.headersMap) {
            if (Object.prototype.hasOwnProperty.call(this.headersMap, key)) {
                const element = this.headersMap[key];
                this.headers = this.headers.set(key, element);
            }
        }
    }

    /*** Add dynamic headers on the go. Don't call this to
     * add request specific headers. Request specific headers should instead be passed
     * with the request call
     */
    addHeader(key: string, value: any): void {
        this.headers = this.headers.set(key, value);
    }

    deleteHeader(key: string): void {
        this.headers = this.headers.delete(key);
    }

    get(
      url: string,
      options: {
        headers?: HttpHeaders | null,
        observe?: string,
        responseType?: "arraybuffer" | "blob" | "text" | "json",
        cacheParams? : any
      } = {}
    ): Observable<OmniServiceBaseResponse> {
        url = this.getURL(url);

        return this.base(options).http.get(url, {
            headers: options.headers,
            observe: options.observe, responseType: options.responseType})
            .pipe(
                map((response: OmniServiceBaseResponse) => response),
                retry(this.retryCount),
                catchError((error: any) => observableThrowError(error))
            );
    }

    getStream(url: string, options: { headers?: HttpHeaders, observe?: string } = {}): Observable<any> {
        options = options || {};
        url = this.getURL(url);
        return this.base((options) ? options : options = {}).http.get(url,
        { headers: options.headers, observe: options.observe })
        .pipe(
            map((response: any) => response),
            retry(this.retryCount),
            catchError((error: any) => observableThrowError(error))
        );
    }

    post(url: string, requestBody: string, options: { headers?: HttpHeaders | null, observe?: string } = {}): Observable<any> {
        url = this.getURL(url);
        return this.base((options) ? options : options = {}).http.post(url, JSON.parse(requestBody),
        { headers: options.headers, observe: options.observe })
        .pipe(
            map((response: any) => response),
            retry(this.retryCount),
            catchError((error: any) => observableThrowError(error))
        );
    }

    put(url: string, requestBody: string, options: { headers?: HttpHeaders, observe?: string } = {}): Observable<any> {
        url = this.getURL(url);
        return this.base((options) ? options : options = {}).http.put(url, JSON.parse(requestBody),
        { headers: options.headers, observe: options.observe })
        .pipe(
            map((response: any) => response),
            retry(this.retryCount),
            catchError((error: any) => observableThrowError(error))
        );
    }

    delete(url: string, options: { headers?: HttpHeaders, observe?: string } = {}): Observable<any> {
        url = this.getURL(url);
        return this.base((options) ? options : options = {}).http.delete(url,
        { headers: options.headers, observe: options.observe })
        .pipe(
            map((response: any) => response),
            retry(this.retryCount),
            catchError((error: any) => observableThrowError(error))
        );
    }

    private base(options?: { headers?: HttpHeaders | null, observe?: string }): any {
      if (!options) {
        return this;
      }

      if (!options.headers) {
            options.headers = this.headers;
        }
      else {
            const keys = options.headers?.keys();
            if (!!keys && keys.length > 0) {
                let tempHeaders = new HttpHeaders();
                const kk = this.headers.keys();
                for (const k of kk) {
                  const value = this.headers.get(k);
                  if (!!value) {
                    tempHeaders = tempHeaders.set(k, value);
                  }
                }

                for (const key of keys) {
                    const element = options.headers?.get(key);
                    if (!!element) {
                      tempHeaders = tempHeaders.set(key, element);
                    }
                }

                options.headers = tempHeaders;
            } else {
                options.headers = this.headers;
            }
        }

        if (!options?.observe) {
            options.observe = 'body';
        }

        return this;
    }

    /**
     * load assets from local asset directory.
     * @param path path within the asset dierectory to load the asset from
     * @param options
     */
    loadAssets(path: string, options: { headers?: HttpHeaders, observe?: string } = {}) : Promise<any> {
        return new Promise((resolve, reject) => {
        if (!environment.localhost) { path = "/content" + path; }
        this.base((options) ? options : options = {}).http.get(path)
            .pipe(
                catchError((): any => {
                    reject(true);
                    return throwError("Failed to load");
                })
            )
            .subscribe((response: any) => {
                resolve(response);
            });
        });
    }

    private getURL(url: string): string {
        if (environment.apiIsProxy) {
            return url;
        }
        else {
            return window.location.origin + url;
        }
    }

    decodeHTMLEntities(html: string): string {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
      }
}
